import { useCallback, useEffect, useMemo } from 'react';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkAdjustCostDialog } from 'src/dialogs/BulkEdits/BulkAdjustCostDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkAdjustCostField,
  BulkChangePriceMode,
  BulkEditPreviewWithDetails,
  BulkEditPurchaseClient,
  BulkEditTicketGroupClient,
  CommandOptionType,
  PurchaseOrderQuery,
} from 'src/WebApiController';

import { PURCHASE_BULK_ADJUST_COST_UPDATE_KEY } from '../../PurchaseActionDropdown.constants';
import { LaunchBulkAdjustCost } from './LaunchBulkAdjustCost';

export const useBulkAdjustCost = (
  filterQuery: PurchaseOrderQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  groupId?: string,
  isEventView?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = groupId
    ? `bulkAdjustCost-event-${groupId}`
    : PURCHASE_BULK_ADJUST_COST_UPDATE_KEY;
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkAdjustCostDialog,
    closeDialog: closeBulkAdjustCostDialog,
  } = useDialog(DialogId.BulkAdjustCost, BulkAdjustCostDialog);

  const onSubmit = useCallback(
    async (
      adjustmentPercent: number | undefined,
      adjustmentAbsoluteAmount: number | undefined,
      changeMode: BulkChangePriceMode,
      targetField: BulkAdjustCostField,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const client = isEventView
              ? new BulkEditTicketGroupClient(activeAccountWebClientConfig)
              : new BulkEditPurchaseClient(activeAccountWebClientConfig);
            const preview = await client.bulkAdjustCostPreview(filterQuery);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkAdjustCostPreview', error, {
              trackErrorData: { filterQuery },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            ).bulkAdjustCost(
              preview!.preview,
              changeMode,
              targetField,
              adjustmentPercent,
              adjustmentAbsoluteAmount,
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkAdjustCostDialog();
            }
          },
          (error) => {
            showErrorDialog('onSubmitAdjustCost', error, {
              trackErrorData: {
                changeMode,
                targetField,
                adjustmentPercent,
                adjustmentAbsoluteAmount,
                preview,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      isEventView,
      activeAccountWebClientConfig,
      filterQuery,
      showErrorDialog,
      updateKey,
      closeBulkAdjustCostDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onLaunchAction = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkAdjustCostDialog({
        updateKey,
        isEventView,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkAdjustCostDialog,
      });
    },
    [
      closeBulkAdjustCostDialog,
      isEventView,
      onSubmit,
      openBulkAdjustCostDialog,
      setIsLoading,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkAdjustCostDialog({
      updateKey,
      isEventView,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkAdjustCostDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.PurchasesBulkAdjustCost,
        onSelect: openDialogStableRef,
      },
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.PurchasesBulkAdjustCost,
      });
    };
  }, [openDialogStableRef, registerCommand, unregisterCommand]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return [
      {
        title: ContentId.CancelPurchasesParenth,
        onClick: onLaunchAction,
        disabled: isLoading,
      },
    ];
  }, [isLoading, onLaunchAction]);

  return {
    dropDown: (
      <LaunchBulkAdjustCost
        key="bulkAdjustCost"
        onClick={onLaunchAction}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
